import {
  baseKeymap,
  chainCommands,
  exitCode,
  joinUp,
  selectParentNode,
} from "prosemirror-commands";
import { redo, undo } from "prosemirror-history";
import { undoInputRule } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import type { Command, Plugin } from "prosemirror-state";
import { redoCommand, undoCommand } from "y-prosemirror";

import type { EditorContext } from "./context";
import {
  insertRule,
  moveBlock,
  setParagraph,
  toggleBlockquote,
  toggleCodeBlock,
  toggleHeading,
} from "./commands/blocks";
import {
  exitCodeBlockOnBackspace,
  indentCodeBlock,
  newlineInCodeBlock,
  outdentCodeBlock,
  selectCodeBlock,
} from "./commands/code";
import { enterToggleBody, leaveToggleBody, toggleOpenState } from "./commands/containers";
import {
  BULLET_LIST,
  TODO_LIST,
  indentListItem,
  liftEmptyListItem,
  moveListItem,
  orderedList,
  outdentListItem,
  splitListItemCommand,
  toggleList,
  toggleTaskChecked,
} from "./commands/lists";
import {
  followLink,
  insertHardBreak,
  insertInlineMathCommand,
  setLink,
  toggleCodeMark,
  toggleEm,
  toggleHighlight,
  toggleStrikethrough,
  toggleStrong,
  toggleUnderline,
} from "./commands/marks";
import { addRowAfter, deleteRow, goToNextCell, isInTable } from "./commands/tables";
import { anchorSelection } from "./plugins/comments";
import { isApplePlatform } from "../lib/platform";
import { HIGHLIGHT_COLORS } from "@wiki/core";
import { openLinkToolbar } from "./plugins/link-toolbar";

export interface KeymapOptions {
  context: EditorContext;
  /** With a Yjs binding, undo/redo come from `y-prosemirror` (§collaboration). */
  collaborative: boolean;
}

/** Wrap an editing command so it is inert while the editor is read-only (§15). */
function editable(context: EditorContext, command: Command): Command {
  return (state, dispatch, view) => (context.readOnly ? false : command(state, dispatch, view));
}

/**
 * `Tab` in the last cell of a table makes a row and moves into it (§10: "Next
 * cell — `Tab` (creates a row at the end of the table)"). Two transactions
 * rather than one: `prosemirror-tables` owns both halves and neither exposes a
 * combined form.
 */
const nextCell: Command = (state, dispatch, view) => {
  if (!isInTable(state)) return false;
  if (goToNextCell(1)(state, dispatch, view)) return true;
  if (!dispatch || !view) return addRowAfter(state, undefined);
  if (!addRowAfter(state, dispatch)) return false;
  goToNextCell(1)(view.state, view.dispatch, view);
  return true;
};

/** `Tab` in a table moves cells; in a list it indents; in code it inserts. */
const tabCommand: Command = chainCommands(
  nextCell,
  indentListItem,
  indentCodeBlock,
  enterToggleBody,
);

const shiftTabCommand: Command = chainCommands(
  (state, dispatch) => (isInTable(state) ? goToNextCell(-1)(state, dispatch) : false),
  outdentListItem,
  outdentCodeBlock,
  leaveToggleBody,
);

const tableRowCommand: Command = (state, dispatch) =>
  isInTable(state) ? addRowAfter(state, dispatch) : false;

const deleteTableRow: Command = (state, dispatch) =>
  isInTable(state) ? deleteRow(state, dispatch) : false;

/**
 * The shortcut table in `editor.md` §10, verbatim, with one platform
 * accommodation: the spec gives `^⇧C` to the code block and `Mod-⇧-C` to the
 * inline code mark, which are the same chord off Apple platforms. There the
 * code block wins, because `^` is explicit in the spec, and inline code keeps
 * its unambiguous `Mod-E`.
 */
export function buildKeymap(options: KeymapOptions): Plugin[] {
  const { context } = options;
  const wrap = (command: Command): Command => editable(context, command);

  const modEnter = chainCommands(
    wrap(toggleTaskChecked),
    wrap(tableRowCommand),
    wrap(toggleOpenState(context.documentId)),
    followLink((href) => {
      context.openLink(href);
    }),
  );

  const bindings: Record<string, Command> = {
    "Mod-b": wrap(toggleStrong),
    "Mod-i": wrap(toggleEm),
    "Mod-u": wrap(toggleUnderline),
    "Mod-d": wrap(toggleStrikethrough),
    "Mod-e": wrap(toggleCodeMark),
    "Mod-Shift-h": wrap(toggleHighlight(HIGHLIGHT_COLORS[0])),
    "Mod-k": wrap((state, dispatch, view) => {
      if (state.selection.empty) return false;
      const applied = setLink("")(state, dispatch, view);
      if (applied && dispatch && view) openLinkToolbar(view);
      return applied;
    }),
    "Mod-Alt-m": (state) => {
      context.onCreateComment(anchorSelection(state));
      return true;
    },
    "Mod-]": chainCommands(wrap(indentListItem), wrap(indentCodeBlock), wrap(toggleBlockquote)),
    "Mod-[": chainCommands(wrap(outdentListItem), wrap(outdentCodeBlock)),
    "Mod-_": wrap(insertRule("---")),
    "Mod-Space": wrap(insertInlineMathCommand),
    "Shift-Enter": chainCommands(wrap(exitCode), wrap(insertHardBreak)),
    "Mod-Enter": modEnter,
    "Mod-Backspace": wrap(deleteTableRow),
    "Mod-Alt-ArrowUp": wrap(moveBlock(-1)),
    "Mod-Alt-ArrowDown": wrap(moveBlock(1)),
    "Alt-ArrowUp": wrap(moveListItem(-1)),
    "Alt-ArrowDown": wrap(moveListItem(1)),
    "Ctrl-Shift-0": wrap(setParagraph),
    "Ctrl-Shift-1": wrap(toggleHeading(1)),
    "Ctrl-Shift-2": wrap(toggleHeading(2)),
    "Ctrl-Shift-3": wrap(toggleHeading(3)),
    "Ctrl-Shift-4": wrap(toggleHeading(4)),
    "Ctrl-Shift-5": wrap(toggleList(orderedList("upper-alpha"))),
    "Ctrl-Shift-6": wrap(toggleList(orderedList("lower-alpha"))),
    "Ctrl-Shift-7": wrap(toggleList(TODO_LIST)),
    "Ctrl-Shift-8": wrap(toggleList(BULLET_LIST)),
    "Ctrl-Shift-9": wrap(toggleList(orderedList("number"))),
    "Ctrl-Shift-\\": wrap(toggleCodeBlock()),
    "Ctrl-Shift-c": wrap(toggleCodeBlock()),
    Tab: wrap(tabCommand),
    "Shift-Tab": wrap(shiftTabCommand),
    // An empty list item leaves the list before anything else looks at the
    // key, so neither `splitListItem` nor the base keymap's block handling can
    // turn the press into yet another empty item (`editor.md` §10).
    Enter: wrap(
      chainCommands(
        // The comment composer submits on Enter. It sits ahead of every block
        // command but behind the suggestion menus, which are a separate plugin
        // earlier in the stack and still get the key while one is open.
        () => context.onSubmit?.() ?? false,
        liftEmptyListItem,
        splitListItemCommand,
        newlineInCodeBlock,
      ),
    ),
    Backspace: wrap(chainCommands(undoInputRule, liftEmptyListItem, exitCodeBlockOnBackspace)),
    "Mod-a": selectCodeBlock,
    "Mod-z": options.collaborative ? undoCommand : undo,
    "Mod-y": options.collaborative ? redoCommand : redo,
    "Shift-Mod-z": options.collaborative ? redoCommand : redo,
    "Alt-ArrowLeft": joinUp,
    Escape: selectParentNode,
  };

  if (isApplePlatform()) {
    // No collision with `^⇧C` on Apple platforms, so the spec's second inline
    // code binding is available there.
    bindings["Mod-Shift-c"] = wrap(toggleCodeMark);
  }

  return [keymap(bindings), keymap(baseKeymap)];
}
